import { z } from 'zod';

import { type RegistrationPayload } from '@/hooks/useRegistration';

const trimmed = (max: number) => {
    return z.string().trim().min(1).max(max);
};

const optionalTrimmed = (max: number) => {
    return z.string().trim().max(max).nullish();
};

/**
 * E.164 shape as produced by `TelephoneInput` (dial prefix + digits). The
 * server runs a full libphonenumber check; this only catches numbers that
 * cannot possibly be valid so the visitor hears about it before submitting.
 */
const E164 = /^\+[1-9]\d{6,14}$/;

const optionalTelephone = z.string().trim().max(32).nullish().refine((value) => {
    return !value || E164.test(value);
}, { message: 'forms.telephone_invalid' });

export const registerSchema = z.object({
    // Company
    company_name: trimmed(100),
    vat_number: optionalTrimmed(32),
    street: trimmed(180),
    postal_code: trimmed(12),
    city: trimmed(120),
    country_code: z.string().length(2),
    telephone: optionalTelephone,

    // Contact person
    first_name: trimmed(100),
    last_name: trimmed(100),
    email: z.string().trim().email().max(255),
    locale: z.string().min(2),

    // Honeypot: humans never see it, so anything but empty is a bot.
    website: z.string().max(255).optional(),
});

export type RegisterValues = z.infer<typeof registerSchema>;

export const registerDefaults = (locale = 'en'): RegisterValues => {
    return {
        city: '',
        company_name: '',
        country_code: '',
        email: '',
        first_name: '',
        last_name: '',
        locale,
        postal_code: '',
        street: '',
        telephone: '',
        vat_number: '',
        website: '',
    };
};

/**
 * Shape the backend expects: empty optionals become null, the country code
 * is upper-cased, and the signed form token travels along.
 */
export const toApiPayload = (values: RegisterValues, formToken: string): RegistrationPayload => {
    return {
        city: values.city,
        company_name: values.company_name,
        country_code: values.country_code.toUpperCase(),
        email: values.email.toLowerCase(),
        first_name: values.first_name,
        form_token: formToken,
        last_name: values.last_name,
        locale: values.locale,
        postal_code: values.postal_code,
        street: values.street,
        telephone: values.telephone ? values.telephone : null,
        vat_number: values.vat_number ? values.vat_number : null,
        website: values.website ?? '',
    };
};
